import { MarketingShell, Pill, SectionHeading, usePageMeta } from './chrome';
import { founderQuote, hero, pricingIntro, pricingTiers, privacyPoints, clubIntro, features, closing, copilotIntro, nzd, roadmap } from './content';
import { CopilotBands } from './CopilotBands';
import { FilmBand, requestFilmPlay } from './FilmBand';
import { HeroDevices, WaterTexture } from './frames';
import { useBrand } from '../lib/brand';
import { HOME_DESCRIPTION, homeTitle } from '../publicSeo';
import { Link } from 'react-router-dom';

const ICONS: Record<string, string> = {
  roster: 'M4 7h16M4 12h16M4 17h10',
  sessions: 'M5 5h14v14H5zM5 9h14M9 5v4',
  times: 'M12 7v6l4 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
  meets: 'M5 19V6m0 0 7 4 7-4v13',
  induction: 'M5 12l5 5L20 7',
  comms: 'M4 6h16v9H7l-3 3z',
  store: 'M5 8h14l-1 11H6L5 8zm3 0V6a4 4 0 0 1 8 0v2',
  site: 'M4 5h16v14H4zM4 9h16',
  privacy: 'M12 3l8 3v6c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V6z',
  lock: 'M8 11V8a4 4 0 0 1 8 0v3M6 11h12v9H6z',
  clubs: 'M4 7h6v10H4zM14 7h6v10h-6z',
  export: 'M12 4v10M8 10l4 4 4-4M5 19h14',
};

const PRIVACY_ICONS = ['lock', 'clubs', 'export'] as const;
const WELLS = ['', 'mkt-well-teal', 'mkt-well-pool'] as const;

function Glyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d={d} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function HomePage() {
  const brand = useBrand();
  usePageMeta(homeTitle(brand.name), HOME_DESCRIPTION);

  return (
    <MarketingShell>
      <section className="mkt-hero-water" data-mkt-surface="dark">
        <WaterTexture />
        <div className="mkt-hero-copy px-6 pb-2 pt-12 text-center md:pt-16">
          <h1 className="mx-auto w-full max-w-copy text-balance text-[clamp(34px,8.6vw,48px)] font-bold leading-[1.08] tracking-[-0.025em] text-white md:text-wrap md:text-[clamp(56px,7.2vw,80px)] md:font-bold md:leading-[1.05] md:tracking-[-0.03em]">
            <span className="md:block">{hero.titleLead}</span>
            <span className="inline md:hidden"> </span>
            <span className="md:block md:whitespace-nowrap">{hero.titleRest}</span>
          </h1>
          <p className="mx-auto mt-5 max-w-copy text-balance text-mkt-sub-sm text-white md:text-mkt-sub">{hero.sub}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Pill to="/contact" surface="dark">
              Book a demo
            </Pill>
            <Pill href="#film" variant="secondary" surface="dark" onClick={requestFilmPlay}>
              Watch the film
            </Pill>
          </div>
        </div>
        <HeroDevices />
      </section>

      <FilmBand />

      <section id="copilot" className="scroll-mt-20">
        <div className="mkt-band mkt-band-tide" data-mkt-surface="dark">
          <div className="px-6 py-mkt-sm md:py-mkt">
            <SectionHeading tone="light" title={copilotIntro.title} sub={copilotIntro.sub} />
            <p className="mt-8 text-center">
              <Link to="/coaches" className="text-mkt-body font-semibold text-white">
                More for coaches
              </Link>
            </p>
          </div>
        </div>
        <div className="mx-auto mt-4 max-w-[1100px] px-6">
          <CopilotBands />
        </div>
      </section>

      <section id="features" className="mkt-band mkt-band-pool scroll-mt-20" data-mkt-surface="dark">
        <div className="px-6 py-mkt-sm md:py-mkt">
          <SectionHeading tone="light" title={clubIntro.title} sub={clubIntro.sub} />
          <ul className="mx-auto mt-12 grid max-w-[1100px] gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <li key={feature.id} className="mkt-card px-6 py-7">
                <span className={`mkt-well ${WELLS[index % WELLS.length]}`}>
                  <Glyph d={ICONS[feature.id] ?? ICONS.privacy} />
                </span>
                <h3 className="mt-5 text-[21px] font-semibold tracking-tight">{feature.title}</h3>
                <p className="mt-2 text-mkt-body text-mkt-ink">{feature.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-10 text-center">
            <Pill to="/features" surface="dark">
              Explore features
            </Pill>
          </p>
        </div>
      </section>

      <section id="roadmap" className="mkt-band mkt-band-deep scroll-mt-20" data-mkt-surface="dark" aria-labelledby="roadmap-title">
        <div className="mx-auto max-w-[1100px] px-6 py-mkt-sm md:py-mkt">
          <h2 id="roadmap-title" className="max-w-[12ch] text-balance text-mkt-section-sm text-white md:text-mkt-section">
            {roadmap.title}
          </h2>
          <p className="mt-5 max-w-[28ch] text-mkt-sub-sm text-white md:text-mkt-sub">{roadmap.note}</p>
          <div className="mt-12 grid items-start gap-5 lg:grid-cols-2">
            <article className="mkt-card px-6 py-7">
              <h3 className="text-[21px] font-semibold tracking-tight text-mkt-teal">Coming soon</h3>
              <ul className="mt-6 space-y-5">
                {roadmap.soon.map((item) => (
                  <li key={item.title}>
                    <p className="text-[17px] font-semibold tracking-tight">{item.title}</p>
                    <p className="mt-1 text-[15px] leading-snug text-mkt-ink">{item.body}</p>
                  </li>
                ))}
              </ul>
            </article>
            <article className="mkt-card px-6 py-7">
              <h3 className="text-[21px] font-semibold tracking-tight text-mkt-teal">On the roadmap</h3>
              <ul className="mt-6 space-y-5">
                {roadmap.later.map((item) => (
                  <li key={item.title}>
                    <p className="text-[17px] font-semibold tracking-tight">{item.title}</p>
                    <p className="mt-1 text-[15px] leading-snug text-mkt-ink">{item.body}</p>
                  </li>
                ))}
              </ul>
            </article>
          </div>
          <div className="mt-10">
            <Pill to="/contact" surface="dark">
              Book a demo
            </Pill>
          </div>
        </div>
      </section>

      <section id="privacy" className="mkt-band mkt-band-tide scroll-mt-20" data-mkt-surface="dark">
        <div className="mx-auto max-w-[1100px] px-6 py-mkt-sm md:py-mkt">
          <h2 className="max-w-[12ch] text-balance text-mkt-section-sm text-white md:text-mkt-section">Private by design.</h2>
          <p className="mt-5 max-w-[24ch] text-mkt-sub-sm text-white md:text-mkt-sub">Built for the New Zealand Privacy Act.</p>
          <ul className="mt-12 grid gap-5 lg:grid-cols-3">
            {privacyPoints.map((point, index) => (
              <li key={point.title} className="mkt-card px-6 py-8">
                <span className={`mkt-well ${WELLS[index % WELLS.length]}`}>
                  <Glyph d={ICONS[PRIVACY_ICONS[index] ?? 'lock'] ?? ICONS.lock} />
                </span>
                <h3 className="mt-6 text-[28px] font-semibold leading-tight tracking-tight">{point.title}</h3>
                <p className="mt-3 text-mkt-body text-mkt-ink">{point.body}</p>
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <Pill to="/privacy" surface="dark">
              Read the privacy policy
            </Pill>
          </div>
        </div>
      </section>

      <section id="story" className="mkt-band mkt-band-story scroll-mt-20" data-mkt-surface="dark">
        <WaterTexture />
        <figure className="relative z-[1] mx-auto max-w-copy px-6 py-mkt-sm md:py-24">
          <blockquote>
            <p className="mkt-quote text-balance text-white">{founderQuote.line}</p>
            <p className="mt-8 max-w-[36ch] text-[21px] leading-snug text-white md:text-[28px]">{founderQuote.body}</p>
          </blockquote>
          <figcaption className="mt-8 text-mkt-body font-semibold text-white">
            {founderQuote.by}
            <span className="mt-1 block font-medium">Coach, Whangārei Academy of Swimming Club</span>
          </figcaption>
          <div className="mt-8">
            <Pill to="/story" surface="dark">
              Read the story
            </Pill>
          </div>
        </figure>
      </section>

      <section id="pricing" className="mkt-band mkt-band-deep scroll-mt-20" data-mkt-surface="dark">
        <div className="px-6 py-mkt-sm md:py-mkt">
          <SectionHeading tone="light" title={pricingIntro.title} sub={pricingIntro.sub} />
          <ul className="mx-auto mt-12 grid max-w-[1100px] gap-5 lg:grid-cols-3">
            {pricingTiers.map((tier) => (
              <li key={tier.id} className={`mkt-card flex flex-col px-6 py-8 ${tier.highlighted ? 'ring-4 ring-[#00AAA4]' : ''}`}>
                <h3 className="text-[21px] font-semibold">{tier.name}</h3>
                <p className="mt-4 text-mkt-section-sm tracking-tight text-mkt-ink">
                  {nzd(tier.priceMonthly)}
                  <span className="text-mkt-body text-mkt-secondary"> / month</span>
                </p>
                <ul className="mt-6 flex-1 space-y-2 text-mkt-body text-mkt-ink">
                  {tier.teaser.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {tier.highlighted ? (
                  <p className="mt-4 text-[14px] font-semibold text-mkt-teal">Most clubs</p>
                ) : (
                  <p className="mt-4 text-[14px] text-transparent" aria-hidden>
                    Most clubs
                  </p>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-10 text-center">
            <Pill to="/pricing" surface="dark">
              Compare plans
            </Pill>
          </p>
        </div>
      </section>

      <section className="mkt-band mkt-band-tide" data-mkt-surface="dark">
        <div className="px-6 py-24 text-center md:py-32">
          <h2 className="mx-auto max-w-copy text-balance text-mkt-section-sm text-white md:text-mkt-section">{closing.title}</h2>
          <p className="mx-auto mt-6 max-w-copy text-mkt-sub-sm text-white md:text-mkt-sub">{closing.sub}</p>
          <div className="mt-8">
            <Pill to="/contact" surface="dark">
              Book a demo
            </Pill>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
